import { useEffect, useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import papi, { formatErr, mediaUrl } from "@/lib/partnerApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Loader2, LogOut, Gift, TrendingUp, Eye, Star, Clock, CheckCircle2, XCircle,
  FileText, Upload, Trash2, Trophy, LayoutDashboard, Image as ImageIcon, Info, Inbox, MessageSquare
} from "lucide-react";

const LOGO = "https://customer-assets-jai6qajn.emergentagent.net/job_buildguard-preview/artifacts/3lbt7aob_nirmantra_ai_icon.png";
const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-700 border-amber-200", approved: "bg-emerald-50 text-emerald-700 border-emerald-200",
  paused: "bg-slate-100 text-slate-600 border-slate-200", hold: "bg-blue-50 text-blue-700 border-blue-200",
  rejected: "bg-red-50 text-red-700 border-red-200", suspended: "bg-red-50 text-red-700 border-red-200",
};

function Shell({ children, onLogout }) {
  return (
    <div className="min-h-screen bg-[#FBFAF9]">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/vendor" className="flex items-center gap-2.5"><img src={LOGO} className="h-9 w-9 rounded-xl" alt="" /><span className="font-display font-extrabold text-lg">Nirmantra<span className="text-[#0E9AC8]">.ai</span> <span className="text-slate-400 text-sm font-medium">Partner</span></span></Link>
          <Button variant="ghost" size="sm" onClick={onLogout} data-testid="vendor-logout" className="text-slate-500 hover:text-[#0E9AC8]"><LogOut className="h-4 w-4 mr-1.5" /> Sign out</Button>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-8">{children}</main>
    </div>
  );
}

export default function VendorPortal() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [dash, setDash] = useState(null);
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();
  const [uploadKind, setUploadKind] = useState("project");
  const [leads, setLeads] = useState(null);

  const loadLeads = async () => {
    try { const { data } = await papi.get("/partner/vendor/leads"); setLeads(data); } catch {}
  };
  const setLeadStatus = async (id, status) => {
    try { await papi.put(`/partner/vendor/leads/${id}/status`, { status }); loadLeads(); toast.success(`Marked ${status}`); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const logout = () => { localStorage.removeItem("partner_token"); localStorage.removeItem("partner_role"); navigate("/vendor/register"); };

  const load = async () => {
    try {
      const [p, d, c] = await Promise.all([papi.get("/partner/vendor/me"), papi.get("/partner/vendor/dashboard"), papi.get("/partner/categories")]);
      setProfile(p.data); setDash(d.data); setCats(c.data);
      const pr = p.data.profile || {};
      setForm({ company_name: pr.company_name || "", about: pr.about || "", intro: pr.intro || "", subcategory: pr.subcategory || "", projects_handled: pr.projects_handled || "", services: pr.services || "", team: pr.team || "", experience_years: pr.experience_years || "", team_size: pr.team_size || "", working_hours: pr.working_hours || "", languages: pr.languages || "", business_address: pr.business_address || "", map_location: pr.map_location || "", service_cities: pr.service_cities || "", service_areas: pr.service_areas || "", categories: p.data.categories || [], social: pr.social || {}, documents: pr.documents || {} });
    } catch (e) {
      if (e.response?.status === 401) navigate("/vendor/register");
    }
  };
  useEffect(() => {
    if (localStorage.getItem("partner_role") !== "vendor") { navigate("/vendor/register"); return; }
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = { categories: form.categories, profile: {
        company_name: form.company_name, about: form.about, intro: form.intro, subcategory: form.subcategory,
        projects_handled: form.projects_handled ? Number(form.projects_handled) : "", services: form.services, team: form.team,
        experience_years: form.experience_years ? Number(form.experience_years) : "", team_size: form.team_size,
        working_hours: form.working_hours, languages: form.languages, business_address: form.business_address, map_location: form.map_location,
        service_cities: form.service_cities, service_areas: form.service_areas, social: form.social, documents: form.documents } };
      const { data } = await papi.put("/partner/vendor/me", body);
      setProfile(data);
      const d = await papi.get("/partner/vendor/dashboard"); setDash(d.data);
      toast.success("Profile saved");
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const upload = async (e) => {
    const f = e.target.files[0]; if (!f) return;
    if (uploadKind === "project" && (profile?.profile?.media || []).filter((m) => m.kind === "project").length >= 20) {
      toast.error("You can upload up to 20 project images");
      if (fileRef.current) fileRef.current.value = "";
      return;
    }
    const fd = new FormData(); fd.append("kind", uploadKind); fd.append("file", f);
    try { const { data } = await papi.post("/partner/vendor/me/media", fd, { headers: { "Content-Type": "multipart/form-data" } }); setProfile(data); toast.success("Uploaded"); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { if (fileRef.current) fileRef.current.value = ""; }
  };
  const delMedia = async (id) => { const { data } = await papi.delete(`/partner/vendor/me/media/${id}`); setProfile(data); };

  const toggleCat = (name) => setForm((f) => ({ ...f, categories: f.categories.includes(name) ? f.categories.filter((c) => c !== name) : [...f.categories, name] }));

  if (!profile || !dash) return <div className="min-h-screen bg-[#FBFAF9] flex items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-[#0E9AC8]" /></div>;

  const media = profile.profile?.media || [];
  const stats = [
    { icon: Eye, label: "Profile Views", value: dash.profile_views }, { icon: TrendingUp, label: "Leads Received", value: dash.leads_received },
    { icon: CheckCircle2, label: "Leads Accepted", value: dash.leads_accepted }, { icon: XCircle, label: "Leads Rejected", value: dash.leads_rejected },
    { icon: FileText, label: "Quotations", value: dash.quotations }, { icon: Star, label: "Rating", value: dash.avg_rating || "—" },
    { icon: Clock, label: "Avg Response", value: dash.avg_response_time }, { icon: Gift, label: "Free Leads", value: dash.free_leads_remaining },
  ];

  return (
    <Shell onLogout={logout}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl font-extrabold text-slate-900">{profile.name}</h1>
          <p className="text-slate-500 text-sm">{profile.email} · {profile.mobile}</p>
        </div>
        <span className={`text-xs font-bold px-3 py-1.5 rounded-full border capitalize w-fit ${STATUS_STYLE[dash.status] || ""}`} data-testid="vendor-status">{dash.status}</span>
      </div>

      <Tabs defaultValue="dashboard">
        <TabsList className="rounded-xl bg-white border border-slate-200 p-1 h-auto">
          <TabsTrigger value="dashboard" data-testid="vtab-dashboard" className="rounded-lg data-[state=active]:bg-[#0E9AC8] data-[state=active]:text-white px-4 py-2"><LayoutDashboard className="h-4 w-4 mr-1.5" /> Dashboard</TabsTrigger>
          <TabsTrigger value="profile" data-testid="vtab-profile" className="rounded-lg data-[state=active]:bg-[#0E9AC8] data-[state=active]:text-white px-4 py-2">Profile</TabsTrigger>
          <TabsTrigger value="portfolio" data-testid="vtab-portfolio" className="rounded-lg data-[state=active]:bg-[#0E9AC8] data-[state=active]:text-white px-4 py-2">Portfolio</TabsTrigger>
          <TabsTrigger value="leads" data-testid="vtab-leads" onClick={loadLeads} className="rounded-lg data-[state=active]:bg-[#0E9AC8] data-[state=active]:text-white px-4 py-2"><Inbox className="h-4 w-4 mr-1.5" /> Leads</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard" className="mt-6 space-y-6">
          <div className="card-premium rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <p className="font-display font-bold text-slate-900">Profile Completion</p>
              <span className="font-display text-2xl font-extrabold text-[#0E9AC8]" data-testid="completion-pct">{profile.completion}%</span>
            </div>
            <div className="mt-3 h-3 w-full bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-[#0E9AC8] rounded-full transition-all" style={{ width: `${profile.completion}%` }} /></div>
            <p className="mt-2 text-sm text-slate-500">{profile.next_milestone ? `Reach ${profile.next_milestone.pct}% to unlock: ${profile.next_milestone.title}` : "All milestones unlocked!"}</p>
          </div>

          <div className="card-premium rounded-2xl p-6">
            <p className="font-display font-bold text-slate-900 flex items-center gap-2"><Trophy className="h-5 w-5 text-[#F5923C]" /> Rewards</p>
            <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
              {profile.rewards_earned.concat(profile.next_milestone ? [profile.next_milestone] : []).map((m, i) => {
                const earned = profile.completion >= m.pct;
                return (
                  <div key={i} className={`rounded-xl border p-4 ${earned ? "border-[#0E9AC8] bg-[#0E9AC8]/5" : "border-dashed border-slate-200 opacity-70"}`} data-testid={`reward-${m.pct}`}>
                    <p className={`font-display text-xl font-extrabold ${earned ? "text-[#0E9AC8]" : "text-slate-400"}`}>{m.pct}%</p>
                    <p className="text-sm font-medium text-slate-800 mt-1">{m.title}</p>
                    {m.reward && <p className="text-xs text-[#F5923C] font-semibold mt-1">🎉 {m.reward}</p>}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((s) => (
              <div key={s.label} className="card-premium rounded-2xl p-5" data-testid={`stat-${s.label}`}>
                <div className="h-9 w-9 rounded-lg bg-[#0E9AC8]/10 flex items-center justify-center mb-3"><s.icon className="h-5 w-5 text-[#0E9AC8]" /></div>
                <p className="font-display text-2xl font-extrabold text-slate-900">{s.value}</p>
                <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="profile" className="mt-6 space-y-6">
          <div className="rounded-2xl border border-[#0E9AC8]/25 bg-[#0E9AC8]/5 p-4 flex items-start gap-3" data-testid="profile-notice">
            <div className="h-9 w-9 rounded-xl bg-[#0E9AC8]/15 flex items-center justify-center shrink-0"><Info className="h-5 w-5 text-[#0E9AC8]" strokeWidth={1.75} /></div>
            <div>
              <p className="font-semibold text-slate-900 text-sm">You can save & submit your profile anytime</p>
              <p className="text-sm text-slate-600 mt-0.5">Your name and mobile are all we need to get started — fill in the rest whenever you like. Only <b>complete profiles get business</b>: the more complete your profile, the more customer leads you receive.</p>
            </div>
          </div>
          <Section title="Basic & Business Information">
            <Field label={`Professional Introduction (max 600 characters) — ${(form.intro || "").length}/600`}>
              <Textarea data-testid="pf-intro" maxLength={600} className="rounded-xl min-h-[90px]" placeholder="Introduce your business to customers — what you do best, your approach and why homeowners trust you." value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} />
            </Field>
            <Grid>
              <Field label="Company Name"><Input data-testid="pf-company" className="rounded-xl" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} /></Field>
              <Field label="Sub-category"><Input data-testid="pf-subcategory" className="rounded-xl" placeholder="e.g. Residential, Luxury" value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} /></Field>
              <Field label="Years of Experience"><Input type="number" className="rounded-xl" value={form.experience_years} onChange={(e) => setForm({ ...form, experience_years: e.target.value })} /></Field>
              <Field label="Total Projects Handled"><Input data-testid="pf-projects" type="number" className="rounded-xl" value={form.projects_handled} onChange={(e) => setForm({ ...form, projects_handled: e.target.value })} /></Field>
              <Field label="Team Size"><Input className="rounded-xl" value={form.team_size} onChange={(e) => setForm({ ...form, team_size: e.target.value })} /></Field>
              <Field label="Working Hours"><Input className="rounded-xl" placeholder="9 AM – 6 PM" value={form.working_hours} onChange={(e) => setForm({ ...form, working_hours: e.target.value })} /></Field>
              <Field label="Languages Spoken"><Input className="rounded-xl" placeholder="Hindi, English" value={form.languages} onChange={(e) => setForm({ ...form, languages: e.target.value })} /></Field>
              <Field label="Google Map Location"><Input className="rounded-xl" placeholder="Maps link" value={form.map_location} onChange={(e) => setForm({ ...form, map_location: e.target.value })} /></Field>
            </Grid>
            <Field label="Services Offered"><Input data-testid="pf-services" className="rounded-xl" placeholder="e.g. Turnkey construction, renovation, site supervision" value={form.services} onChange={(e) => setForm({ ...form, services: e.target.value })} /></Field>
            <Field label="Key Team / Members"><Input data-testid="pf-team" className="rounded-xl" placeholder="e.g. Lead architect, 2 site engineers, skilled crew" value={form.team} onChange={(e) => setForm({ ...form, team: e.target.value })} /></Field>
            <Field label="Business Address"><Input className="rounded-xl" value={form.business_address} onChange={(e) => setForm({ ...form, business_address: e.target.value })} /></Field>
            <Field label="About Company"><Textarea className="rounded-xl" value={form.about} onChange={(e) => setForm({ ...form, about: e.target.value })} /></Field>
          </Section>

          <Section title="Service Areas">
            <Grid>
              <Field label="Service Cities"><Input data-testid="pf-cities" className="rounded-xl" placeholder="Delhi, Noida" value={form.service_cities} onChange={(e) => setForm({ ...form, service_cities: e.target.value })} /></Field>
              <Field label="Service Areas / Localities"><Input className="rounded-xl" value={form.service_areas} onChange={(e) => setForm({ ...form, service_areas: e.target.value })} /></Field>
            </Grid>
          </Section>

          <Section title="Services / Categories">
            <div className="flex flex-wrap gap-2">
              {cats.map((c) => (
                <button key={c.id} type="button" onClick={() => toggleCat(c.name)} data-testid={`cat-${c.id}`}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${form.categories.includes(c.name) ? "border-[#0E9AC8] bg-[#0E9AC8]/10 text-[#0E9AC8]" : "border-slate-200 text-slate-600 hover:border-[#0E9AC8]"}`}>{c.name}</button>
              ))}
            </div>
          </Section>

          <Section title="Digital Presence (optional)">
            <Grid>
              {["website", "google_business", "instagram", "facebook", "linkedin", "youtube"].map((k) => (
                <Field key={k} label={k.replace("_", " ").replace(/\b\w/g, (l) => l.toUpperCase())}>
                  <Input className="rounded-xl" value={form.social[k] || ""} onChange={(e) => setForm({ ...form, social: { ...form.social, [k]: e.target.value } })} />
                </Field>
              ))}
            </Grid>
          </Section>

          <Section title="Documents (optional)">
            <Grid>
              {["gst", "pan", "aadhaar", "trade_license", "company_registration"].map((k) => (
                <Field key={k} label={k.replace("_", " ").toUpperCase()}>
                  <Input className="rounded-xl" value={form.documents[k] || ""} onChange={(e) => setForm({ ...form, documents: { ...form.documents, [k]: e.target.value } })} />
                </Field>
              ))}
            </Grid>
          </Section>

          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            <Button data-testid="pf-save" onClick={save} disabled={saving} className="bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl h-11 px-8">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save & Submit Profile"}
            </Button>
            <p className="text-sm text-slate-500">Submit now and keep improving — only complete profiles get customer leads.</p>
          </div>
        </TabsContent>

        <TabsContent value="portfolio" className="mt-6 space-y-6">
          <div className="card-premium rounded-2xl p-6">
            <p className="font-display font-bold text-slate-900">Upload media</p>
            <p className="text-sm text-slate-500 mt-1">Add your logo, cover, profile photo and up to 20 project images. Photos are optional — but a rich portfolio wins more customers.</p>
            <div className="flex flex-wrap items-center gap-3 mt-4">
              <select data-testid="media-kind" value={uploadKind} onChange={(e) => setUploadKind(e.target.value)} className="rounded-xl border border-slate-200 px-3 py-2 text-sm">
                <option value="logo">Company Logo</option><option value="cover">Cover Image</option><option value="profile_photo">Profile Photo</option>
                <option value="project">Project Image</option><option value="before_after">Before/After</option><option value="brochure">Brochure/PDF</option>
              </select>
              <input ref={fileRef} type="file" data-testid="media-input" onChange={upload} accept=".png,.jpg,.jpeg,.webp,.pdf" className="hidden" />
              <Button data-testid="media-upload-btn" onClick={() => fileRef.current?.click()} className="bg-[#0E9AC8] hover:bg-[#0B7EA4] text-white rounded-xl"><Upload className="h-4 w-4 mr-2" /> Choose file</Button>
              <span className="text-sm text-slate-500">{media.filter((m) => m.kind === "project").length} / 20 project images <span className="text-slate-400">(optional)</span></span>
            </div>
          </div>
          {media.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {media.map((m) => (
                <div key={m.id} className="card-premium rounded-xl overflow-hidden group relative" data-testid={`media-${m.id}`}>
                  {m.content_type?.includes("image") ? <img src={mediaUrl(profile.id, m.id)} alt="" className="w-full h-32 object-cover" /> : <div className="w-full h-32 flex items-center justify-center bg-slate-50"><FileText className="h-8 w-8 text-slate-300" /></div>}
                  <div className="p-2 flex items-center justify-between"><span className="text-[10px] uppercase text-slate-400">{m.kind.replace("_", " ")}</span>
                    <button onClick={() => delMedia(m.id)} className="text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button></div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
        <TabsContent value="leads" className="mt-6 space-y-6">
          {!leads ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-[#0E9AC8]" /></div> : (
            <>
              <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                {[["Total", leads.total], ["New", leads.new], ["Contacted", leads.contacted], ["Pending", leads.pending], ["Converted", leads.converted], ["Closed", leads.closed]].map(([l, val]) => (
                  <div key={l} className="card-premium rounded-xl p-4" data-testid={`lead-stat-${l}`}><p className="font-display text-2xl font-extrabold text-[#0E9AC8]">{val}</p><p className="text-xs text-slate-500">{l}</p></div>
                ))}
              </div>
              <div className="card-premium rounded-2xl overflow-hidden">
                {leads.leads.length === 0 ? <div className="p-14 text-center text-slate-500"><Inbox className="h-9 w-9 text-slate-300 mx-auto mb-2" />No leads yet. Complete your profile to receive customer leads.</div> : (
                  <div className="overflow-x-auto"><table className="w-full text-sm" data-testid="vendor-leads-table">
                    <thead className="bg-slate-50 text-slate-500 text-xs uppercase"><tr><th className="text-left p-3">Customer</th><th className="text-left p-3">Requirement</th><th className="text-left p-3">City</th><th className="text-left p-3">Date</th><th className="text-left p-3">Status</th></tr></thead>
                    <tbody className="divide-y divide-slate-100">
                      {leads.leads.map((l) => (
                        <tr key={l.id} className="hover:bg-slate-50">
                          <td className="p-3"><p className="font-medium text-slate-800">{l.customer_name}</p><p className="text-xs text-slate-400">{l.customer_mobile}</p></td>
                          <td className="p-3 text-slate-600 max-w-[280px]"><div className="line-clamp-2">{l.project_requirement}</div></td>
                          <td className="p-3 text-slate-600">{l.city}</td>
                          <td className="p-3 text-xs text-slate-500 whitespace-nowrap">{l.date} {l.time}</td>
                          <td className="p-3">
                            <select value={l.status} data-testid={`vlead-status-${l.id}`} onChange={(e) => setLeadStatus(l.id, e.target.value)} className="text-xs font-semibold px-2 py-1 rounded-lg border border-slate-200 capitalize">
                              {["new", "contacted", "pending", "closed", "converted"].map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table></div>
                )}
              </div>
            </>
          )}
        </TabsContent>
      </Tabs>
    </Shell>
  );
}
const Section = ({ title, children }) => (<div className="card-premium rounded-2xl p-6"><h3 className="font-display text-lg font-bold text-slate-900 mb-4">{title}</h3><div className="space-y-4">{children}</div></div>);
const Grid = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
const Field = ({ label, children }) => <div><Label className="text-xs text-slate-500">{label}</Label><div className="mt-1.5">{children}</div></div>;
